import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./PortalTooltip.module.css";

const PortalTooltip = ({ isHidden, children, triggerRef, onClose }) => {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement: "bottom",
    arrowLeft: 0,
    arrowTop: 0,
  });

  const updatePosition = useCallback(() => {
    if (!isHidden && triggerRef?.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let tooltipHeight = 300;
      let tooltipWidth = 500;

      if (tooltipRef.current) {
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        tooltipHeight = tooltipRect.height || 300;
        tooltipWidth = tooltipRect.width || 500;
      }

      const spacing = 8;
      const triggerCenterX = triggerRect.left + triggerRect.width / 2;

      let tooltipTop, tooltipLeft, placement, arrowLeft, arrowTop;
    
      const extraBottomBuffer = 20;
      let isShowToolTipBottom = triggerRect.bottom + spacing + tooltipHeight + extraBottomBuffer < viewportHeight;
      if (isShowToolTipBottom) {
        tooltipTop = triggerRect.bottom + spacing + 3;
        placement = "bottom";
        arrowTop = tooltipTop - 8;
      } else {
        tooltipTop = triggerRect.top - spacing - tooltipHeight - 3;
        placement = "top";
        arrowTop = tooltipTop + tooltipHeight - 2;
      }

      arrowLeft = triggerCenterX - 10;

      tooltipLeft = triggerCenterX - tooltipWidth / 2;
      if (tooltipLeft + tooltipWidth > viewportWidth) {
        tooltipLeft = viewportWidth - tooltipWidth - 16;
      }
      if (tooltipLeft < 16) {
        tooltipLeft = 16;
      }

      setPosition({
        top: tooltipTop,
        left: tooltipLeft,
        placement,
        arrowLeft,
        arrowTop,
      });
    }
  }, [isHidden, triggerRef]);

  useEffect(() => {
    updatePosition();
    if (!isHidden) {
      const timeoutId = setTimeout(updatePosition, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [updatePosition, isHidden]);

  useEffect(() => {
    if (!isHidden) {
      const handleScrollOrResize = () => {
        updatePosition();
      };
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);

      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
  }, [isHidden, updatePosition]);

  if (isHidden) return null;

  return createPortal(
    <>
      <div className={styles.tooltipOverlay} onClick={onClose} />
      <div
        ref={tooltipRef}
        className={`${styles.tooltip} ${
          styles[`tooltip-${position.placement}`]
        }`}
        style={{
          top: position.top,
          left: position.left,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
      <div
        className={`${styles.arrow} ${styles[`arrow-${position.placement}`]}`}
        style={{
          position: "fixed",
          left: position.arrowLeft,
          top: position.arrowTop,
          zIndex: 1002,
        }}
      />
    </>,
    document.body
  );
};

export default PortalTooltip;
